import type {
  TimeEntry,
  NewTimeEntry,
  Person,
  PersonOption,
  Project,
  BankHoliday,
  Asset,
  BillOutRate,
  TimesheetStatus,
  PeriodMonth,
  ISODateString,
  TimeEntryAuditRecord,
} from './timesheet.types';
import type {
  Order,
  OrderLineItem,
  OrderApprovalLog,
  OrderComment,
  COSHHEntry,
  OrderAttachment,
  AttachmentCategory,
  OrderFilters,
  NewOrderData,
  OrderData,
  NewLineItemData,
  NewApprovalData,
  NewCommentData,
} from './ordering.types';

// ─── Filter / Query DTOs ──────────────────────────────────────────────────────

export interface TimeEntryFilters {
  personEmail?: string;
  periodMonth?: PeriodMonth;
  status?: TimesheetStatus;
  managerEmail?: string;
  dateFrom?: ISODateString;
  dateTo?: ISODateString;
}

export interface PeopleFilters {
  active?: boolean;
  role?: string;
}

export interface PersonData {
  fullName: string;
  m365Email: string;
  department: string;
  role: string;
  lineManager: string;
  lineManagerEmail: string;
  backupApprover: string;
  backupApproverEmail: string;
  superApprover: string;
  superApproverEmail: string;
  costType?: string;
  hourlyRate?: number | null;
  exemptFromReminders?: boolean;
  exemptionStart?: string | null;
  exemptionEnd?: string | null;
  isTimesheetMgmtGroup: boolean;
  active: boolean;
  purchaseApprovalLimit?: number | null;
  isChemicalApprover?: boolean;
  isOrderMgmtGroup?: boolean;
  isCFO?: boolean;
  canApproveOnBehalfOfCEO?: boolean;
}

export interface ProjectData {
  projectCode: string;
  projectName: string;
  type: string;
  costCentre: string;
  projectOwnerName: string;
  projectOwnerEmail: string;
  assignedPMName: string;
  assignedPMEmail: string;
  startDate?: string | null;
  endDate?: string | null;
  estimatedHours?: number | null;
  assignedTeamEmails: string;
  assignedTeamNames: string;
  isResearchDevelopment: boolean;
  status: string;
  description: string;
}

export interface AssetData {
  title: string;
  description: string;
  responsiblePerson: string;
  responsiblePersonEmail: string;
  responsiblePersonName: string;
  responsiblePeopleEmails?: string[];
  responsiblePeopleNames?: string[];
  responsiblePersonId?: number;
  active: boolean;
}

export interface CostTypeItem {
  id: string;
  name: string;
  costRate: number;
}

export interface BillOutRateData {
  costGroup: string;
  billRate: number;
  costRate: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  notes?: string;
}

export interface BillOutRateFilters {
  costGroup?: string;
}

// ─── Batch Operation Results ──────────────────────────────────────────────────

export interface BatchItemResult {
  id: string;
  error?: string;
}

export interface BatchSaveResult {
  succeeded: string[];
  failed: BatchItemResult[];
  /** Full TimeEntry objects for entries that were newly created (had a 'new-' id). */
  created?: TimeEntry[];
}

// ─── Workflow Trigger Payload ─────────────────────────────────────────────────

export enum WorkflowTriggerType {
  SubmitMonth = 'SUBMIT_MONTH',
  ApproveMonth = 'APPROVE_MONTH',
  RejectMonth = 'REJECT_MONTH',
  SuperApproveMonth = 'SUPER_APPROVE_MONTH',
  LockMonth = 'LOCK_MONTH',
  UnlockMonth = 'UNLOCK_MONTH',
  SendChaseEmail = 'SEND_CHASE_EMAIL',
}

export type WorkflowActionStatus =
  | 'Submitted'
  | 'Manager Approved'
  | 'Rejected'
  | 'Super Approved'
  | 'Draft';

export type ApproverRole = 'Manager' | 'Backup Approver' | 'Super Approver' | 'Unlock Group';

export type WorkflowSource =
  | 'MyTimesheet'
  | 'AssetTimesheet'
  | 'ApprovalDashboard'
  | 'SuperApprover'
  | 'UnlockGroup';

export interface WorkflowTriggerPayload {
  triggerType: WorkflowTriggerType;
  /** SP PEOPLE list item ID — used as the reference key for queries. */
  personId: string;
  /** Employee display name — written to UserName field. */
  userName: string;
  /** Employee M365 email — used to resolve SP User field and written to UserEmail. */
  userEmail: string;
  periodMonth: PeriodMonth;
  actionStatus: WorkflowActionStatus;
  isFinalState: boolean;
  approverName: string;
  approverEmail: string;
  approverRole: ApproverRole;
  reason?: string;
  rejectionComment?: string;
  source: WorkflowSource;
  totalHours?: number;
  weekSummary?: string;
}

// ─── Chase Notification (audit + PowerAutomate trigger) ──────────────────────

export enum ChaseNotificationStatus {
  /** New row, PowerAutomate has not yet sent the email. */
  Pending = 'Pending',
  /** PA has successfully dispatched the email. */
  Sent = 'Sent',
  /** PA attempted send but failed. */
  Failed = 'Failed',
}

export interface ChaseNotificationPayload {
  personName: string;
  personEmail: string;
  periodMonth: PeriodMonth;
}

export interface ChaseNotification {
  readonly id: string;
  personName: string;
  personEmail: string;
  periodMonth: PeriodMonth;
  status: ChaseNotificationStatus;
  /** SP Created column — used for audit ordering. */
  created: string;
}

export interface ChaseNotificationFilters {
  personEmail?: string;
  periodMonth?: PeriodMonth;
  status?: ChaseNotificationStatus;
  /** Return only entries created on/after this ISO date. */
  since?: string;
}

// ─── Order Workflow Triggers ──────────────────────────────────────────────────

export enum OrderWorkflowTriggerType {
  OrderSubmitted           = 'ORDER_SUBMITTED',
  LMApprovalRequest        = 'LM_APPROVAL_REQUEST',
  ChemicalApprovalRequest  = 'CHEMICAL_APPROVAL_REQUEST',
  EscalatedApprovalRequest = 'ESCALATED_APPROVAL_REQUEST',
  CEOApprovalRequest       = 'CEO_APPROVAL_REQUEST',
  CFOApprovalRequest       = 'CFO_APPROVAL_REQUEST',
  OrderApprovedToOM        = 'ORDER_APPROVED_TO_OM',
  OrderSelfApprovedToOM    = 'ORDER_SELF_APPROVED_TO_OM',
  OrderRejected            = 'ORDER_REJECTED',
  OrderQueried             = 'ORDER_QUERIED',
  OrderPlaced              = 'ORDER_PLACED',
  FYIViewerInvite          = 'FYI_VIEWER_INVITE',
  AddComment               = 'ADD_COMMENT',
  ForReviewUpdatedByRequester = 'FOR_REVIEW_TO_OM',
}

export interface OrderWorkflowTriggerPayload {
  triggerType: OrderWorkflowTriggerType;
  orderRef: string;
  orderId: number;
  orderTitle: string;
  /** Sterling equivalent amount used for approval routing. */
  orderAmount: number;
  recipientEmail: string;
  recipientName: string;
  requesterEmail: string;
  requesterName: string;
  /** Matches the Title column in the Email Templates SP list. */
  templateName: string;
  /** Rejection reason, query text, or other contextual note. */
  note?: string;
  /** Comment text stored in the Comments (note) column of Order Workflow Triggers. */
  comment?: string;
  projectName?: string;
}

// ─── Notification Read Types ──────────────────────────────────────────────────

export interface WorkflowTriggerNotification {
  readonly id: string;
  title: string;
  actionType: string;
  status: string;
  periodMonth: string;
  userName: string;
  userEmail: string;
  approverName: string;
  approverEmail: string;
  approverRole: string;
  source: string;
  isFinalState: boolean;
  reason?: string;
  rejectionComment?: string;
  totalHours?: number;
  timestamp: string;
  hideNotification: boolean;
  read: boolean;
  created: string;
}

export interface OrderWorkflowTriggerNotification {
  readonly id: string;
  title: string;
  triggerType: string;
  orderRef: string;
  orderId: number;
  orderTitle: string;
  orderAmount: number;
  recipientName: string;
  recipientEmail: string;
  requesterName: string;
  requesterEmail: string;
  status: string;
  note?: string;
  timestamp: string;
  hideNotification: boolean;
  read: boolean;
  created: string;
}

export interface NotificationFilters {
  userEmail?: string;
  since?: string;
  limit?: number;
}

// ─── Connector Interface ──────────────────────────────────────────────────────

/**
 * The single abstraction over the data backend.
 *
 * Phase 1: SharePointConnector (SharePoint REST API)
 * Phase 2+: DataverseConnector (same interface, drop-in swap via ConnectorContext)
 *
 * Components and hooks MUST only depend on this interface, never on a concrete class.
 */
export interface ITimesheetConnector {
  // ── Time Entries ────────────────────────────────────────────────────────────

  getTimeEntries(filters: TimeEntryFilters, listName?: string): Promise<TimeEntry[]>;

  createTimeEntry(entry: NewTimeEntry, listName?: string): Promise<TimeEntry>;

  updateTimeEntry(id: string, updates: Partial<TimeEntry>, listName?: string): Promise<void>;

  /**
   * Applies the same field updates to multiple entries concurrently.
   * All updates are attempted; throws only after all complete if any failed.
   */
  batchUpdateEntries(
    updates: Array<{ id: string; fields: Partial<TimeEntry> }>,
    listName?: string
  ): Promise<void>;

  /**
   * Updates multiple entries concurrently (max 5 in-flight).
   * Each entry may be a create (id starts with 'new-') or update.
   */
  batchSaveEntries(
    entries: Array<TimeEntry | NewTimeEntry>,
    listName?: string
  ): Promise<BatchSaveResult>;

  /**
   * Deletes multiple time entries by ID from the given list.
   * Entries with ids starting with 'new-' (never persisted) are silently skipped.
   */
  deleteTimeEntries(ids: string[], listName?: string): Promise<void>;

  /**
   * Saves audit records for unlock-related activities.
   * Records are saved to the Timesheet Unlock Audit list.
   */
  batchSaveAuditRecords(records: TimeEntryAuditRecord[]): Promise<BatchSaveResult>;

  // ── Status Transitions (business operations) ─────────────────────────────────

  /**
   * Enqueues a workflow trigger via SharePoint list (no direct webhook exposure).
   */
  triggerWorkflow(payload: WorkflowTriggerPayload): Promise<void>;

  /**
   * Enqueues an order workflow trigger via SharePoint list (mirrors triggerWorkflow for orders).
   */
  triggerOrderWorkflow(payload: OrderWorkflowTriggerPayload): Promise<void>;

  /**
   * Writes a row to the Chase Notifications list. PowerAutomate listens for
   * new items and dispatches the reminder email, updating Status/SentAt.
   * Also serves as the audit trail for every chase sent.
   */
  sendChaseNotification(payload: ChaseNotificationPayload): Promise<ChaseNotification>;

  /** Reads chase notification audit history. */
  getChaseNotifications(filters?: ChaseNotificationFilters): Promise<ChaseNotification[]>;

  // ── Notifications (read + hide) ──────────────────────────────────────────────

  /** Reads visible workflow trigger notifications, newest first. */
  getWorkflowTriggerNotifications(filters?: NotificationFilters): Promise<WorkflowTriggerNotification[]>;

  /** Reads visible order workflow trigger notifications, newest first. */
  getOrderWorkflowTriggerNotifications(filters?: NotificationFilters): Promise<OrderWorkflowTriggerNotification[]>;

  /** Sets HideNotification=true on a WORKFLOW TRIGGERS list item. */
  hideWorkflowTriggerNotification(id: string): Promise<void>;

  /** Sets HideNotification=true on an ORDERS WORKFLOW TRIGGERS list item. */
  hideOrderWorkflowTriggerNotification(id: string): Promise<void>;

  /** Sets Read=true on multiple WORKFLOW TRIGGERS list items. */
  markWorkflowTriggerNotificationsRead(ids: string[]): Promise<void>;

  /** Sets Read=true on multiple ORDER WORKFLOW TRIGGERS list items. */
  markOrderTriggerNotificationsRead(ids: string[]): Promise<void>;

  // ── Reference Data ──────────────────────────────────────────────────────────

  getProjectsForUser(userId: string, periodMonth?: PeriodMonth): Promise<Project[]>;

  getAllActiveProjects(): Promise<Project[]>;

  getAllProjects(): Promise<Project[]>;

  createProject(data: ProjectData): Promise<Project>;

  updateProject(id: string, data: Partial<ProjectData>): Promise<void>;

  deleteProject(id: string): Promise<void>;

  getCurrentUser(): Promise<Person>;

  getPerson(personId: string): Promise<Person>;

  getPersonByEmail(email: string): Promise<Person | null>;

  getPeople(filters?: PeopleFilters): Promise<Person[]>;

  getDirectReports(managerEmail: string): Promise<Person[]>;

  getBackupReports(backupApproverEmail: string): Promise<Person[]>;

  getSuperApproverReports(superApproverEmail: string): Promise<Person[]>;

  getBankHolidays(year: number): Promise<BankHoliday[]>;

  createPerson(data: PersonData): Promise<Person>;

  updatePerson(id: string, data: Partial<PersonData>): Promise<void>;

  getAssets(): Promise<Asset[]>;

  createAsset(data: AssetData): Promise<Asset>;

  updateAsset(id: string, data: Partial<AssetData>): Promise<void>;

  /**
   * Reads the "Asset Timesheet Permitted Group" list and returns the set of
   * lowercase emails allowed to fill in any asset's timesheet (in addition
   * to each asset's primary/responsible owner).
   */
  getAssetPermittedMembers(): Promise<Set<string>>;

  /**
   * Reads the "Unlock Timesheet Permitted Group" list and returns the set of
   * lowercase emails permitted to unlock timesheets.
   */
  getUnlockPermittedMembers(): Promise<Set<string>>;

  /** Reads the "Order Management Group" list (PersonEmail text column) and returns lowercase email set. */
  getOrderMgmtGroupMembers(): Promise<Set<string>>;

  /**
   * Reads the "Security Configurations" list for the "Bill-Out Rates" row and
   * returns the set of lowercase emails listed in TargetUsers.
   */
  getBillOutRatesPermittedMembers(): Promise<Set<string>>;
  getOrderingPermittedMembers(): Promise<Set<string>>;

  // ── Bill-Out Rates ──────────────────────────────────────────────────────────

  /** Returns all rate periods, optionally filtered by cost group. */
  getBillOutRates(filters?: BillOutRateFilters): Promise<BillOutRate[]>;

  createBillOutRate(data: BillOutRateData): Promise<BillOutRate>;

  updateBillOutRate(id: string, data: Partial<BillOutRateData>): Promise<void>;

  deleteBillOutRate(id: string): Promise<void>;

  /**
   * Returns the rate record whose effectiveFrom <= date and
   * (effectiveTo IS NULL or effectiveTo >= date). When multiple
   * overlap, the one with the latest effectiveFrom wins.
   * Returns null when no rate exists for that group on that date.
   */
  lookupBillOutRate(costGroup: string, date: ISODateString): Promise<BillOutRate | null>;

  // ── Lookup / Master Lists ────────────────────────────────────────────────────

  getRoleOptions(): Promise<string[]>;
  getCostTypeOptions(): Promise<string[]>;
  getCostTypes(): Promise<CostTypeItem[]>;
  getProjectTypeOptions(): Promise<string[]>;
  getCostCentreOptions(): Promise<string[]>;
  getUnitOfMeasureOptions(): Promise<string[]>;
  getProjectStatusOptions(): Promise<string[]>;
  getTimesheetStatusOptions(): Promise<string[]>;
  getLineManagerOptions(): Promise<PersonOption[]>;
  getBackupApproverOptions(): Promise<PersonOption[]>;
  getSuperApproverOptions(): Promise<PersonOption[]>;

  // ── Ordering ─────────────────────────────────────────────────────────────────

  getOrders(filters?: OrderFilters): Promise<Order[]>;
  getOrderById(id: string): Promise<Order>;
  createOrder(data: NewOrderData): Promise<Order>;
  updateOrder(id: string, data: Partial<OrderData>): Promise<void>;

  getOrderLineItems(orderId: number): Promise<OrderLineItem[]>;
  saveOrderLineItems(orderId: number, items: NewLineItemData[]): Promise<OrderLineItem[]>;
  deleteOrderLineItem(id: string): Promise<void>;

  getOrderApprovalLog(orderId: number): Promise<OrderApprovalLog[]>;
  addApprovalDecision(data: NewApprovalData): Promise<OrderApprovalLog>;

  getOrderComments(orderId: number): Promise<OrderComment[]>;
  addOrderComment(data: NewCommentData): Promise<OrderComment>;

  getCOSHHRegister(): Promise<COSHHEntry[]>;

  // ── Order Attachments (document library) ──────────────────────────────────

  /**
   * Creates the per-order subfolder if needed, then uploads the file to
   * "Order Attachments/{orderRef}/".  Returns the stored attachment metadata.
   */
  uploadOrderAttachment(
    orderId: number,
    orderRef: string,
    file: File,
    category: AttachmentCategory
  ): Promise<OrderAttachment>;

  /**
   * Lists all files in "Order Attachments/{orderRef}/".
   * Returns [] when the folder doesn't exist yet (no uploads).
   */
  getOrderAttachments(orderRef: string): Promise<OrderAttachment[]>;

  /**
   * Fetches a file using the stored Bearer token and returns it as a Blob
   * so the UI can trigger a browser download without exposing the token.
   */
  downloadOrderAttachment(serverRelUrl: string): Promise<Blob>;
}
